import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Grid,
  MenuItem,
  Alert,
  Snackbar,
  Divider,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorDisplay from '../components/common/ErrorDisplay';
import { businessConfigService } from '../services/businessConfigService';
import { ROUTES } from '../constants/routes';

const BUSINESS_CATEGORIES = [
  'Healthcare',
  'Beauty & Wellness',
  'Fitness',
  'Professional Services',
  'Education',
  'Automotive',
  'Home Services',
  'Other',
];

/**
 * Business Settings Page - Edit business profile configuration
 */
const BusinessSettingsPage = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  
  const [formData, setFormData] = useState({
    business_name: '',
    category: '',
    description: '',
    active: true,
    contact_info: {
      email: '',
      phone: '',
      address: '',
      website: '',
    },
    branding: {
      logo_url: '',
      primary_color: '#1976d2',
      cover_image_url: '',
    },
  });

  useEffect(() => {
    const fetchBusinessConfig = async () => {
      try {
        setLoading(true);
        const config = await businessConfigService.getBusinessConfig(customerId);
        setFormData({
          business_name: config.business_name || '',
          category: config.category || '',
          description: config.description || '',
          active: config.active !== undefined ? config.active : true,
          contact_info: {
            email: config.contact_info?.email || '',
            phone: config.contact_info?.phone || '',
            address: config.contact_info?.address || '',
            website: config.contact_info?.website || '',
          },
          branding: {
            logo_url: config.branding?.logo_url || '',
            primary_color: config.branding?.primary_color || '#1976d2',
            cover_image_url: config.branding?.cover_image_url || '',
          },
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (customerId) {
      fetchBusinessConfig();
    }
  }, [customerId]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleContactInfoChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      contact_info: {
        ...prev.contact_info,
        [field]: value,
      },
    }));
  };

  const handleBrandingChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      branding: {
        ...prev.branding,
        [field]: value,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.business_name.trim()) {
      setError(t('error.generic'));
      return;
    }
    
    if (!formData.category) {
      setError(t('error.generic'));
      return;
    }

    try {
      setSaving(true);
      setError(null);
      
      await businessConfigService.updateBusinessConfig(customerId, formData);
      
      setSuccessMessage(t('businessSettings.successMessage'));
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate(ROUTES.HOME);
  };

  if (loading) {
    return <LoadingSpinner message={t('businessSettings.loadingMessage')} />;
  }

  if (error && !formData.business_name) {
    return <ErrorDisplay message={error} />;
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mb: 2 }}
        >
          {t('businessSettings.back')}
        </Button>
        <Typography 
          variant="h4" 
          gutterBottom
          sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}
        >
          {t('businessSettings.title')}
        </Typography>
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ fontSize: { xs: '0.813rem', sm: '0.875rem' } }}
        >
          {t('businessSettings.subtitle')}
        </Typography>
      </Box>

      <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 } }}>
        <form onSubmit={handleSubmit}>
          {/* Basic Information */}
          <Typography 
            variant="h6" 
            gutterBottom
            sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
          >
            {t('businessSettings.basicInformation')}
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label={t('businessSettings.businessName')}
                value={formData.business_name}
                onChange={(e) => handleInputChange('business_name', e.target.value)}
                helperText={t('businessSettings.businessNameHelper')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                select
                label={t('businessSettings.category')}
                value={formData.category}
                onChange={(e) => handleInputChange('category', e.target.value)}
              >
                {BUSINESS_CATEGORIES.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label={t('businessSettings.status')}
                value={formData.active}
                onChange={(e) => handleInputChange('active', e.target.value === 'true')}
              >
                <MenuItem value={true}>{t('businessSettings.active')}</MenuItem>
                <MenuItem value={false}>{t('businessSettings.inactive')}</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t('businessSettings.description')}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                helperText={t('businessSettings.descriptionHelper')}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Contact Information */}
          <Typography 
            variant="h6" 
            gutterBottom
            sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
          >
            {t('businessSettings.contactInformation')}
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="email"
                label={t('businessSettings.email')}
                value={formData.contact_info.email}
                onChange={(e) => handleContactInfoChange('email', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('businessSettings.phone')}
                value={formData.contact_info.phone}
                onChange={(e) => handleContactInfoChange('phone', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('businessSettings.address')}
                value={formData.contact_info.address}
                onChange={(e) => handleContactInfoChange('address', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('businessSettings.website')}
                value={formData.contact_info.website}
                onChange={(e) => handleContactInfoChange('website', e.target.value)}
                helperText={t('businessSettings.websiteHelper')}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Branding */}
          <Typography 
            variant="h6" 
            gutterBottom
            sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
          >
            {t('businessSettings.branding')}
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('businessSettings.logoUrl')}
                value={formData.branding.logo_url}
                onChange={(e) => handleBrandingChange('logo_url', e.target.value)}
                helperText={t('businessSettings.logoUrlHelper')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="color"
                label={t('businessSettings.primaryColor')}
                value={formData.branding.primary_color}
                onChange={(e) => handleBrandingChange('primary_color', e.target.value)}
                helperText={t('businessSettings.primaryColorHelper')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('businessSettings.coverImageUrl')}
                value={formData.branding.cover_image_url}
                onChange={(e) => handleBrandingChange('cover_image_url', e.target.value)}
                helperText={t('businessSettings.coverImageUrlHelper')}
              />
            </Grid>
          </Grid>

          {/* Error Display */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexDirection: { xs: 'column', sm: 'row' } }}>
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={saving}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={saving}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              {saving ? t('businessSettings.saving') : t('businessSettings.saveChanges')}
            </Button>
          </Box>
        </form>
      </Paper>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default BusinessSettingsPage;
